import { useEffect, useRef, useState, useCallback } from "react";
import { applyPageMetadata } from "../../../lib/seo";
import { GameEngine } from "./engine/GameEngine";
import { CANVAS_WIDTH, CANVAS_HEIGHT, MAP_WIDTH, MAP_HEIGHT } from "./config/gameConfig";
import { anomaliAudio } from "./audio/AnomaliAudio";
import { SaveSystem } from "./save/SaveSystem";
import "./bertahan-dari-anomali.css";

// Radar Mini-Map Renderer
function renderMiniMap(radarCanvas: HTMLCanvasElement, eng: GameEngine) {
  const rctx = radarCanvas.getContext("2d");
  if (!rctx) return;

  const rw = radarCanvas.width;
  const rh = radarCanvas.height;
  rctx.clearRect(0, 0, rw, rh);

  const cx = rw / 2;
  const cy = rh / 2;
  const scale = rw / Math.max(MAP_WIDTH, MAP_HEIGHT);

  // Map boundary circle background
  rctx.fillStyle = "rgba(18, 26, 32, 0.9)";
  rctx.beginPath();
  rctx.arc(cx, cy, rw / 2 - 2, 0, Math.PI * 2);
  rctx.fill();

  // River trace
  rctx.fillStyle = "#1e4655";
  rctx.fillRect(290 * scale, 0, 100 * scale, rh);

  // Enemy dots (Red)
  for (const enemy of eng.enemies) {
    if (enemy.isDead) continue;
    const ex = enemy.x * scale;
    const ey = enemy.y * scale;
    rctx.fillStyle = enemy.type === "heavy" ? "#ff2222" : "#ff6666";
    rctx.beginPath();
    rctx.arc(ex, ey, enemy.type === "heavy" ? 3.5 : 2.5, 0, Math.PI * 2);
    rctx.fill();
  }

  // Player arrow (Yellow)
  const px = eng.player.x * scale;
  const py = eng.player.y * scale;
  rctx.save();
  rctx.translate(px, py);
  rctx.rotate(eng.player.facingAngle);
  rctx.fillStyle = "#ffdd33";
  rctx.beginPath();
  rctx.moveTo(5, 0);
  rctx.lineTo(-3, -3);
  rctx.lineTo(-1, 0);
  rctx.lineTo(-3, 3);
  rctx.closePath();
  rctx.fill();
  rctx.restore();
}

export function BertahanDariAnomaliPage() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const radarCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const animFrameIdRef = useRef<number | null>(null);

  // Keyboard state
  const keysPressed = useRef<{ [key: string]: boolean }>({});

  // Touch Joystick state
  const joystickTouchId = useRef<number | null>(null);
  const joystickOrigin = useRef<{ x: number; y: number } | null>(null);
  const joystickVector = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [thumbPos, setThumbPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // UI Reactive States
  const [playerHp, setPlayerHp] = useState<number>(420);
  const [playerMaxHp, setPlayerMaxHp] = useState<number>(420);
  const [playerEnergy, setPlayerEnergy] = useState<number>(120);
  const [playerMaxEnergy, setPlayerMaxEnergy] = useState<number>(120);
  const [potionsLeft, setPotionsLeft] = useState<number>(3);
  const [defeatedCount, setDefeatedCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(6);

  // Cooldowns for UI
  const [cdFlash, setCdFlash] = useState<number>(0);
  const [cdCrescent, setCdCrescent] = useState<number>(0);
  const [cdSpin, setCdSpin] = useState<number>(0);
  const [cdSpecial, setCdSpecial] = useState<number>(0);
  const [cdPotion, setCdPotion] = useState<number>(0);
  const [isTargetLocked, setIsTargetLocked] = useState<boolean>(false);

  // Modals
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isVictory, setIsVictory] = useState<boolean>(false);
  const [isGameOver, setIsGameOver] = useState<boolean>(false);
  const [showSkillGuide, setShowSkillGuide] = useState<boolean>(false);

  // Settings
  const [sfxOn, setSfxOn] = useState<boolean>(true);
  const [bgmOn, setBgmOn] = useState<boolean>(true);
  const [screenShakeOn, setScreenShakeOn] = useState<boolean>(true);
  const [damageNumbersOn, setDamageNumbersOn] = useState<boolean>(true);
  const [touchControlsEnabled, setTouchControlsEnabled] = useState<boolean>(false);

  // Initialize Game & Engine
  useEffect(() => {
    // Auto-detect mobile/touchscreen device
    const isTouch = typeof window !== "undefined" && (("ontouchstart" in window) || navigator.maxTouchPoints > 0);
    setTouchControlsEnabled(isTouch);

    applyPageMetadata({
      title: "Bertahan Dari Anomali | Top-Down Pixel Action RPG",
      description:
        "Play Bertahan Dari Anomali, a top-down 2D pixel action RPG. Master 8-directional movement, Flash Step dodges, enemy attack telegraphs, and target lock in the ancient ruins.",
      canonicalPath: "/games/bertahan-dari-anomali",
    });

    const engine = new GameEngine();
    engineRef.current = engine;

    // Load initial settings
    setSfxOn(engine.saveData.settings.sfxEnabled);
    setBgmOn(engine.saveData.settings.bgmEnabled);
    setScreenShakeOn(engine.saveData.settings.screenShake);
    setDamageNumbersOn(engine.saveData.settings.damageNumbers);

    anomaliAudio.sfxEnabled = engine.saveData.settings.sfxEnabled;
    anomaliAudio.bgmEnabled = engine.saveData.settings.bgmEnabled;
    anomaliAudio.setSfxVolume(engine.saveData.settings.sfxVolume);
    anomaliAudio.setBgmVolume(engine.saveData.settings.bgmVolume);

    // Audio Autoplay resume listener on first user touch/click
    const handleFirstInteraction = () => {
      anomaliAudio.startBgm();
      window.removeEventListener("pointerdown", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
    };
    window.addEventListener("pointerdown", handleFirstInteraction);
    window.addEventListener("keydown", handleFirstInteraction);

    // Main 60 FPS Game Loop
    let lastTime = performance.now();
    const loop = (time: number) => {
      const dt = Math.min(0.05, (time - lastTime) / 1000);
      lastTime = time;

      if (engineRef.current && canvasRef.current) {
        const eng = engineRef.current;
        const ctx = canvasRef.current.getContext("2d");

        // 1. Gather Keyboard inputs
        let kx = 0;
        let ky = 0;
        if (keysPressed.current["KeyW"] || keysPressed.current["ArrowUp"]) ky -= 1;
        if (keysPressed.current["KeyS"] || keysPressed.current["ArrowDown"]) ky += 1;
        if (keysPressed.current["KeyA"] || keysPressed.current["ArrowLeft"]) kx -= 1;
        if (keysPressed.current["KeyD"] || keysPressed.current["ArrowRight"]) kx += 1;

        // Combine with Touch Joystick
        const jx = joystickVector.current.x;
        const jy = joystickVector.current.y;
        const combinedX = Math.abs(jx) > 0.05 ? jx : kx;
        const combinedY = Math.abs(jy) > 0.05 ? jy : ky;

        // Target lock angle if target exists
        let targetAngle: number | null = null;
        if (eng.targetLockSystem.lockedTarget && !eng.targetLockSystem.lockedTarget.isDead) {
          const t = eng.targetLockSystem.lockedTarget;
          targetAngle = Math.atan2(t.y - eng.player.y, t.x - eng.player.x);
        }

        // Apply movement
        eng.player.handleMovementInput(combinedX, combinedY, targetAngle, eng.map, dt);

        // Step engine physics and combat
        eng.update(dt);

        // Render Frame
        if (ctx) {
          ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
          eng.render(ctx, time);
        }

        // Render Radar Mini-map
        if (radarCanvasRef.current) {
          renderMiniMap(radarCanvasRef.current, eng);
        }

        // Update React UI states
        setPlayerHp(eng.player.hp);
        setPlayerMaxHp(eng.player.maxHp);
        setPlayerEnergy(Math.round(eng.player.energy));
        setPlayerMaxEnergy(eng.player.maxEnergy);
        setPotionsLeft(eng.player.potionsLeft);
        setDefeatedCount(eng.defeatedCount);
        setTotalCount(eng.targetTotalCount);

        setCdFlash(Math.max(0, eng.player.cdFlashStep));
        setCdCrescent(Math.max(0, eng.player.cdCrescent));
        setCdSpin(Math.max(0, eng.player.cdSpin));
        setCdSpecial(Math.max(0, eng.player.cdSpecial));
        setCdPotion(Math.max(0, eng.player.cdPotion));
        setIsTargetLocked(Boolean(eng.targetLockSystem.lockedTarget && !eng.targetLockSystem.lockedTarget.isDead));

        if (eng.isVictory) setIsVictory(true);
        if (eng.isGameOver) setIsGameOver(true);
      }

      animFrameIdRef.current = requestAnimationFrame(loop);
    };

    animFrameIdRef.current = requestAnimationFrame(loop);

    return () => {
      if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
      anomaliAudio.stopBgm();
      window.removeEventListener("pointerdown", handleFirstInteraction);
      window.removeEventListener("keydown", handleFirstInteraction);
    };
  }, []);

  const togglePause = useCallback(() => {
    if (engineRef.current) {
      engineRef.current.isPaused = !engineRef.current.isPaused;
      setIsPaused(engineRef.current.isPaused);
    }
  }, []);

  // ------------------------------------------------------------------------
  // KEYBOARD CONTROLLER
  // ------------------------------------------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      keysPressed.current[e.code] = true;

      const eng = engineRef.current;
      if (!eng) return;

      const targetAngle = eng.targetLockSystem.lockedTarget
        ? Math.atan2(
            eng.targetLockSystem.lockedTarget.y - eng.player.y,
            eng.targetLockSystem.lockedTarget.x - eng.player.x
          )
        : null;

      // Normal Attack: J or Z
      if (e.code === "KeyJ" || e.code === "KeyZ") {
        eng.player.triggerNormalAttack(targetAngle);
      }
      // Flash Step: Space or K or Shift
      else if (e.code === "Space" || e.code === "KeyK") {
        e.preventDefault();
        eng.player.triggerFlashStep(joystickVector.current.x, joystickVector.current.y, targetAngle);
      }
      // Crescent Slash: Q or U
      else if (e.code === "KeyQ" || e.code === "KeyU") {
        eng.player.triggerCrescentSlash(targetAngle);
      }
      // Spin Strike: E or I
      else if (e.code === "KeyE" || e.code === "KeyI") {
        eng.player.triggerSpinStrike();
      }
      // Special: R or L
      else if (e.code === "KeyR" || e.code === "KeyL") {
        eng.player.triggerSpecialAttack(targetAngle);
      }
      // Target Lock: Tab or F
      else if (e.code === "Tab" || e.code === "KeyF") {
        e.preventDefault();
        eng.targetLockSystem.toggleLock(eng.player.x, eng.player.y, eng.enemies);
      }
      // Potion: 1 or C
      else if (e.code === "Digit1" || e.code === "KeyC") {
        eng.player.usePotion();
      }
      // Pause: Escape
      else if (e.code === "Escape") {
        togglePause();
      }
      // Debug Hitboxes: F2
      else if (e.code === "F2") {
        eng.debugHitboxes = !eng.debugHitboxes;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      keysPressed.current[e.code] = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [togglePause]);

  // ------------------------------------------------------------------------
  // TOUCH JOYSTICK HANDLERS
  // ------------------------------------------------------------------------
  const handleJoystickTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (joystickTouchId.current !== null) return;
    const touch = e.changedTouches[0];
    joystickTouchId.current = touch.identifier;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    joystickOrigin.current = { x: centerX, y: centerY };
    updateJoystick(touch.clientX, touch.clientY);
  };

  const handleJoystickTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      if (touch.identifier === joystickTouchId.current) {
        updateJoystick(touch.clientX, touch.clientY);
        break;
      }
    }
  };

  const handleJoystickTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === joystickTouchId.current) {
        joystickTouchId.current = null;
        joystickOrigin.current = null;
        joystickVector.current = { x: 0, y: 0 };
        setThumbPos({ x: 0, y: 0 });
        break;
      }
    }
  };

  const updateJoystick = (clientX: number, clientY: number) => {
    if (!joystickOrigin.current) return;
    const dx = clientX - joystickOrigin.current.x;
    const dy = clientY - joystickOrigin.current.y;
    const maxRadius = 45;
    const dist = Math.hypot(dx, dy);

    const clampedDist = Math.min(dist, maxRadius);
    const angle = Math.atan2(dy, dx);
    const thumbX = Math.cos(angle) * clampedDist;
    const thumbY = Math.sin(angle) * clampedDist;

    setThumbPos({ x: thumbX, y: thumbY });
    joystickVector.current = {
      x: thumbX / maxRadius,
      y: thumbY / maxRadius,
    };
  };

  // ------------------------------------------------------------------------
  // ACTION BUTTON TRIGGERS
  // ------------------------------------------------------------------------
  const getLockAngle = useCallback((): number | null => {
    const eng = engineRef.current;
    if (!eng || !eng.targetLockSystem.lockedTarget || eng.targetLockSystem.lockedTarget.isDead) return null;
    const t = eng.targetLockSystem.lockedTarget;
    return Math.atan2(t.y - eng.player.y, t.x - eng.player.x);
  }, []);

  const getMouseAngle = useCallback((e: React.PointerEvent<HTMLCanvasElement>): number | null => {
    const eng = engineRef.current;
    const canvas = canvasRef.current;
    if (!eng || !canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_WIDTH / rect.width;
    const scaleY = CANVAS_HEIGHT / rect.height;
    const screenX = (e.clientX - rect.left) * scaleX;
    const screenY = (e.clientY - rect.top) * scaleY;
    const worldX = screenX + eng.camera.x;
    const worldY = screenY + eng.camera.y;
    return Math.atan2(worldY - eng.player.y, worldX - eng.player.x);
  }, []);

  const handleAttack = (explicitAngle: number | null = null) => {
    const angle = getLockAngle() ?? explicitAngle;
    engineRef.current?.player.triggerNormalAttack(angle);
  };

  const handleFlashStep = (explicitAngle: number | null = null) => {
    const eng = engineRef.current;
    if (!eng) return;
    eng.player.triggerFlashStep(joystickVector.current.x, joystickVector.current.y, getLockAngle() ?? explicitAngle);
  };

  const handleCrescent = (explicitAngle: number | null = null) => {
    engineRef.current?.player.triggerCrescentSlash(getLockAngle() ?? explicitAngle);
  };

  const handleSpin = () => {
    engineRef.current?.player.triggerSpinStrike();
  };

  const handleSpecial = (explicitAngle: number | null = null) => {
    engineRef.current?.player.triggerSpecialAttack(getLockAngle() ?? explicitAngle);
  };

  const handleTargetLock = () => {
    const eng = engineRef.current;
    if (!eng) return;
    eng.targetLockSystem.toggleLock(eng.player.x, eng.player.y, eng.enemies);
  };

  const handlePotion = () => {
    engineRef.current?.player.usePotion();
  };

  const restartGame = () => {
    engineRef.current?.restart();
    setIsPaused(false);
    setIsVictory(false);
    setIsGameOver(false);
  };

  const toggleSfxSetting = () => {
    const active = anomaliAudio.toggleSfx();
    setSfxOn(active);
    if (engineRef.current) {
      engineRef.current.saveData.settings.sfxEnabled = active;
      SaveSystem.save(engineRef.current.saveData);
    }
  };

  const toggleBgmSetting = () => {
    const active = anomaliAudio.toggleBgm();
    setBgmOn(active);
    if (engineRef.current) {
      engineRef.current.saveData.settings.bgmEnabled = active;
      SaveSystem.save(engineRef.current.saveData);
    }
  };

  const toggleScreenShake = () => {
    setScreenShakeOn((prev) => {
      const next = !prev;
      if (engineRef.current) {
        engineRef.current.camera.screenShake = next ? 0 : 0;
        engineRef.current.saveData.settings.screenShake = next;
        SaveSystem.save(engineRef.current.saveData);
      }
      return next;
    });
  };

  return (
    <main className="anomali-wrapper">
      <div className={`anomali-stage ${touchControlsEnabled ? "anomali-touch-active" : "anomali-desktop-mode"}`}>
        {/* Game Canvas */}
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="anomali-canvas"
          onContextMenu={(e) => e.preventDefault()}
          onPointerDown={(e) => {
            if (e.button === 2) {
              e.preventDefault();
              handleFlashStep(getMouseAngle(e));
            } else {
              handleAttack(getMouseAngle(e));
            }
          }}
        />

        {/* TOP LEFT HUD: Profile, HP/Energy Bars, Objective */}
        <div className="anomali-hud-top-left">
          <div className="anomali-profile-card">
            <div className="anomali-portrait">
              <div className="anomali-portrait-art" />
              <div className="anomali-level-badge">Lv. 24</div>
            </div>
            <div className="anomali-bars">
              {/* HP Bar */}
              <div className="anomali-bar-container">
                <div
                  className="anomali-bar-fill anomali-hp-fill"
                  style={{ width: `${Math.max(0, (playerHp / playerMaxHp) * 100)}%` }}
                />
                <div className="anomali-bar-label">{`${playerHp} / ${playerMaxHp}`}</div>
              </div>
              {/* Energy Bar */}
              <div className="anomali-bar-container">
                <div
                  className="anomali-bar-fill anomali-energy-fill"
                  style={{ width: `${Math.max(0, (playerEnergy / playerMaxEnergy) * 100)}%` }}
                />
                <div className="anomali-bar-label">{`${playerEnergy} / ${playerMaxEnergy}`}</div>
              </div>
            </div>
          </div>

          {/* Objective Card */}
          <div className="anomali-objective-card">
            <span className="anomali-obj-icon">!</span>
            <div>
              <div className="anomali-obj-title">Bertahan Dari Anomali</div>
              <div className="anomali-obj-desc">
                Kalahkan Anomali ({defeatedCount} / {totalCount})
              </div>
            </div>
          </div>
        </div>

        {/* TOP RIGHT HUD: Radar Mini-Map & Action Buttons */}
        <div className="anomali-hud-top-right">
          <div className="anomali-radar-container">
            <div className="anomali-radar-location">Ancient Ruins · 15:32</div>
            <div className="anomali-radar">
              <span className="anomali-radar-cardinal anomali-radar-n">N</span>
              <span className="anomali-radar-cardinal anomali-radar-s">S</span>
              <span className="anomali-radar-cardinal anomali-radar-w">W</span>
              <span className="anomali-radar-cardinal anomali-radar-e">E</span>
              <canvas
                ref={radarCanvasRef}
                width={68}
                height={68}
                className="anomali-radar-canvas"
              />
            </div>
          </div>

          <div className="anomali-top-buttons">
            <button
              className="anomali-icon-btn"
              onClick={() => togglePause()}
              title="Pause / Settings"
            >
              ⚙
            </button>
            <button
              className="anomali-icon-btn"
              onClick={() => setShowSkillGuide(true)}
              title="Skill Guide"
            >
              📜
            </button>
            <button
              className="anomali-icon-btn"
              onClick={() => toggleBgmSetting()}
              title="Toggle Music"
            >
              {bgmOn ? "🎵" : "🔇"}
            </button>
            <button
              className={`anomali-icon-btn ${touchControlsEnabled ? "active" : ""}`}
              onClick={() => setTouchControlsEnabled((prev) => !prev)}
              title={touchControlsEnabled ? "Sembunyikan Virtual Joystick (Desktop Mode)" : "Tampilkan Virtual Joystick (Touch Mode)"}
            >
              📱
            </button>
          </div>
        </div>

        {/* BOTTOM LEFT: Virtual Analog Joystick */}
        <div
          className="anomali-joystick-zone"
          onTouchStart={handleJoystickTouchStart}
          onTouchMove={handleJoystickTouchMove}
          onTouchEnd={handleJoystickTouchEnd}
        >
          <div className="anomali-joystick-base">
            <span className="anomali-joystick-arrow anomali-j-up">▲</span>
            <span className="anomali-joystick-arrow anomali-j-down">▼</span>
            <span className="anomali-joystick-arrow anomali-j-left">◀</span>
            <span className="anomali-joystick-arrow anomali-j-right">▶</span>
            <div
              className="anomali-joystick-thumb"
              style={{
                transform: `translate(${thumbPos.x}px, ${thumbPos.y}px)`,
              }}
            />
          </div>
        </div>

        {/* BOTTOM RIGHT: Action Buttons Cluster */}
        <div className="anomali-action-cluster">
          {/* 1. Large Normal Attack */}
          <button
            className="anomali-btn-attack"
            onPointerDown={(e) => {
              e.stopPropagation();
              handleAttack();
            }}
            title="Normal Attack (J / Click)"
          >
            ⚔
          </button>

          {/* 2. Flash Step */}
          <button
            className="anomali-skill-btn anomali-pos-flash"
            onPointerDown={(e) => {
              e.stopPropagation();
              handleFlashStep();
            }}
            title="Flash Step (Space)"
          >
            <span className="anomali-key-hint">SPC</span>
            🏃
            {cdFlash > 0 && (
              <div className="anomali-cooldown-overlay">{cdFlash.toFixed(1)}</div>
            )}
          </button>

          {/* 3. Crescent Slash */}
          <button
            className="anomali-skill-btn anomali-pos-crescent"
            onPointerDown={(e) => {
              e.stopPropagation();
              handleCrescent();
            }}
            title="Crescent Slash (Q)"
          >
            <span className="anomali-key-hint">Q</span>
            🌙
            {cdCrescent > 0 && (
              <div className="anomali-cooldown-overlay">{cdCrescent.toFixed(1)}</div>
            )}
          </button>

          {/* 4. Spin Strike */}
          <button
            className="anomali-skill-btn anomali-pos-spin"
            onPointerDown={(e) => {
              e.stopPropagation();
              handleSpin();
            }}
            title="Spin Strike (E)"
          >
            <span className="anomali-key-hint">E</span>
            🌀
            {cdSpin > 0 && (
              <div className="anomali-cooldown-overlay">{cdSpin.toFixed(1)}</div>
            )}
          </button>

          {/* 5. Special Attack ("Anomaly Break") */}
          <button
            className="anomali-skill-btn anomali-pos-special"
            onPointerDown={(e) => {
              e.stopPropagation();
              handleSpecial();
            }}
            title="Special: Anomaly Break (R)"
          >
            <span className="anomali-key-hint">R</span>
            ⚡
            {cdSpecial > 0 && (
              <div className="anomali-cooldown-overlay">{cdSpecial.toFixed(1)}</div>
            )}
          </button>

          {/* 6. Target Lock */}
          <button
            className="anomali-skill-btn anomali-pos-lock"
            onPointerDown={(e) => {
              e.stopPropagation();
              handleTargetLock();
            }}
            title="Target Lock (Tab)"
          >
            <span className="anomali-key-hint">TAB</span>
            🎯
          </button>

          {/* 7. Healing Potion */}
          <button
            className="anomali-skill-btn anomali-pos-potion"
            onPointerDown={(e) => {
              e.stopPropagation();
              handlePotion();
            }}
            title="Potion (1)"
          >
            <span className="anomali-key-hint">1</span>
            🧪
            <span className="anomali-badge">{potionsLeft}</span>
            {cdPotion > 0 && (
              <div className="anomali-cooldown-overlay">{cdPotion.toFixed(1)}</div>
            )}
          </button>
        </div>

        {/* DESKTOP ACTION HOTBAR */}
        <div className="anomali-desktop-hotbar">
          <div
            className="anomali-hotbar-slot slot-attack"
            onClick={() => handleAttack()}
            title="Tebasan Pedang (Klik Kiri / J)"
          >
            <span className="anomali-hotbar-key">L-CLK</span>
            ⚔
          </div>
          <div
            className="anomali-hotbar-slot"
            onClick={() => handleFlashStep()}
            title="Flash Step Dash (Klik Kanan / Spasi)"
          >
            <span className="anomali-hotbar-key">SPC</span>
            🏃
            {cdFlash > 0 && <div className="anomali-hotbar-cd">{cdFlash.toFixed(1)}</div>}
          </div>
          <div
            className="anomali-hotbar-slot"
            onClick={() => handleCrescent()}
            title="Crescent Wave (Q)"
          >
            <span className="anomali-hotbar-key">Q</span>
            🌙
            {cdCrescent > 0 && <div className="anomali-hotbar-cd">{cdCrescent.toFixed(1)}</div>}
          </div>
          <div
            className="anomali-hotbar-slot"
            onClick={() => handleSpin()}
            title="Spin Strike (E)"
          >
            <span className="anomali-hotbar-key">E</span>
            🌀
            {cdSpin > 0 && <div className="anomali-hotbar-cd">{cdSpin.toFixed(1)}</div>}
          </div>
          <div
            className="anomali-hotbar-slot slot-special"
            onClick={() => handleSpecial()}
            title="Anomaly Break (R)"
          >
            <span className="anomali-hotbar-key">R</span>
            ⚡
            {cdSpecial > 0 && <div className="anomali-hotbar-cd">{cdSpecial.toFixed(1)}</div>}
          </div>
          <div
            className={`anomali-hotbar-slot ${isTargetLocked ? "slot-locked" : ""}`}
            onClick={() => handleTargetLock()}
            title="Target Lock (Tab)"
          >
            <span className="anomali-hotbar-key">TAB</span>
            🎯
          </div>
          <div
            className="anomali-hotbar-slot"
            onClick={() => handlePotion()}
            title="Healing Potion (1)"
          >
            <span className="anomali-hotbar-key">1</span>
            🧪
            <span className="anomali-hotbar-badge">{potionsLeft}</span>
            {cdPotion > 0 && <div className="anomali-hotbar-cd">{cdPotion.toFixed(1)}</div>}
          </div>
        </div>

        {/* Desktop Helper Bar */}
        <div className="anomali-desktop-helper">
          [WASD] Gerak · [Klik Kiri/J] Tebas · [Klik Kanan/Spasi] Dash · [Q] Crescent · [E] Spin · [R] Special · [Tab] Lock · [1] Potion
        </div>

        {/* ================================================================ */}
        {/* MODAL: PAUSE & SETTINGS                                          */}
        {/* ================================================================ */}
        {isPaused && (
          <div className="anomali-modal-backdrop">
            <div className="anomali-modal-card">
              <h2 className="anomali-modal-title">Permainan Dijeda</h2>
              <p className="anomali-modal-desc">
                Atur volume dan kenyamanan visual game "Bertahan Dari Anomali".
              </p>

              <div className="anomali-setting-row">
                <span>Musik Latar (BGM)</span>
                <button
                  className="anomali-setting-toggle"
                  onClick={() => toggleBgmSetting()}
                >
                  {bgmOn ? "AKTIF" : "MATI"}
                </button>
              </div>

              <div className="anomali-setting-row">
                <span>Efek Suara (SFX)</span>
                <button
                  className="anomali-setting-toggle"
                  onClick={() => toggleSfxSetting()}
                >
                  {sfxOn ? "AKTIF" : "MATI"}
                </button>
              </div>

              <div className="anomali-setting-row">
                <span>Getaran Layar (Screen Shake)</span>
                <button
                  className="anomali-setting-toggle"
                  onClick={() => toggleScreenShake()}
                >
                  {screenShakeOn ? "AKTIF" : "MATI"}
                </button>
              </div>

              <div className="anomali-setting-row">
                <span>Angka Kerusakan (Damage Numbers)</span>
                <button
                  className="anomali-setting-toggle"
                  onClick={() => setDamageNumbersOn((v) => !v)}
                >
                  {damageNumbersOn ? "AKTIF" : "MATI"}
                </button>
              </div>

              <div className="anomali-modal-buttons">
                <button className="anomali-btn-primary" onClick={() => togglePause()}>
                  Lanjutkan
                </button>
                <button className="anomali-btn-secondary" onClick={() => restartGame()}>
                  Ulangi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* MODAL: VICTORY                                                   */}
        {/* ================================================================ */}
        {isVictory && (
          <div className="anomali-modal-backdrop">
            <div className="anomali-modal-card">
              <h2 className="anomali-modal-title">Misi Selesai!</h2>
              <p className="anomali-modal-desc">
                Semua ancaman Anomali di reruntuhan telah berhasil dimusnahkan! Reruntuhan kini kembali damai.
              </p>
              <div className="anomali-setting-row">
                <span>Anomali Terkalahkan</span>
                <span style={{ color: "#ffd978", fontWeight: "bold" }}>6 / 6</span>
              </div>
              <div className="anomali-modal-buttons">
                <button className="anomali-btn-primary" onClick={() => restartGame()}>
                  Main Lagi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* MODAL: GAME OVER                                                 */}
        {/* ================================================================ */}
        {isGameOver && (
          <div className="anomali-modal-backdrop">
            <div className="anomali-modal-card">
              <h2 className="anomali-modal-title" style={{ color: "#ff4444" }}>
                Karakter Gugur
              </h2>
              <p className="anomali-modal-desc">
                Anomali di reruntuhan terlalu kuat. Perhatikan Red Zone telegraph musuh dan gunakan Flash Step tepat waktu!
              </p>
              <div className="anomali-modal-buttons">
                <button className="anomali-btn-primary" onClick={() => restartGame()}>
                  Coba Lagi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* MODAL: SKILL GUIDE (MATCHING THE BOTTOM BAR IN USER REFERENCE)   */}
        {/* ================================================================ */}
        {showSkillGuide && (
          <div className="anomali-modal-backdrop">
            <div className="anomali-modal-card" style={{ maxWidth: "560px" }}>
              <h2 className="anomali-modal-title">SKILL INFORMATION (PLAYER ONLY)</h2>
              <div className="anomali-skill-guide-list">
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">1. Normal Attack (J / Klik)</div>
                  <div className="anomali-guide-text">
                    Serangan dasar 3-hit combo dengan pedang. Cepat dan stabil.
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">2. Flash Step (Spasi)</div>
                  <div className="anomali-guide-text">
                    Berpindah posisi dengan cepat dengan 3 bayangan (afterimages). Memiliki invulnerability frame (i-frame).
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">3. Crescent Slash (Q)</div>
                  <div className="anomali-guide-text">
                    Melancarkan tebasan melengkung berenergi ke depan yang menembus beberapa musuh dalam satu garis.
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">4. Spin Strike (E)</div>
                  <div className="anomali-guide-text">
                    Berputar 360° menyerang musuh di sekitar karakter. Sangat efektif saat dikepung banyak anomali.
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">5. Special Attack (R)</div>
                  <div className="anomali-guide-text">
                    "Anomaly Break": Menghimpun energi dan melancarkan tebasan mematikan dengan efek hitstop besar.
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">6. Target Lock (Tab)</div>
                  <div className="anomali-guide-text">
                    Mengunci sasaran musuh terdekat dengan indikator berlian merah kecil di atas kepala. Memfokuskan serangan.
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">7. Potion (1)</div>
                  <div className="anomali-guide-text">
                    Memulihkan sejumlah besar HP seketika. Jumlah penggunaan terbatas (maksimal 3 botol).
                  </div>
                </div>
                <div className="anomali-guide-item">
                  <div className="anomali-guide-name">8. Enemy Red Zone (Telegraph)</div>
                  <div className="anomali-guide-text">
                    Tanda merah transparan di tanah memberitahu area serangan musuh sebelum dilancarkan. Menghindarlah sebelum serangannya tiba!
                  </div>
                </div>
              </div>
              <div className="anomali-modal-buttons">
                <button
                  className="anomali-btn-primary"
                  onClick={() => setShowSkillGuide(false)}
                >
                  Tutup Panduan
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
